import { UI, HOLDING_COLORS, HOLDING_REST } from "../constants/tokens";
import LTR from "./LTR";

// ─── The holdings ring (sprint 16, stage 3) ─────────────────────────────────
// Replaces the allocation bar on the portfolio card. A donut of real position
// weights: the seven largest holdings each in a hue of their own
// (HOLDING_COLORS, by descending weight), and everything past them as one grey
// slice, "אחרות". The middle says how many holdings the ring is made of.
// Beside it, the legend: a dot, the ticker and the share of the portfolio,
// largest first, with "אחרות" last.
//
// `rows`: [{ symbol, weight }] · weight in percent of the portfolio, the same
// numbers the holding cards show as "נתח מהתיק". `holdings`: how many holdings
// the portfolio has, for the middle · the same count as the cards below it,
// including a holding with no quote, which has no slice (the card says so
// under the ring).

export const RING_TOP = HOLDING_COLORS.length;   // 7

// The slices, largest first: the top seven and, when there are more, one
// "אחרות" with how many holdings it holds.
export function ringSlices(rows = [], top = RING_TOP) {
  const sorted = rows.filter(r => r.weight > 0).sort((a, b) => b.weight - a.weight);
  const slices = sorted.slice(0, top).map((r, i) => ({ key: r.symbol, label: r.symbol, weight: r.weight, color: HOLDING_COLORS[i] }));
  const rest = sorted.slice(top);
  if (rest.length) {
    slices.push({ key: "__rest", label: "אחרות", weight: rest.reduce((a, r) => a + r.weight, 0), color: HOLDING_REST, count: rest.length, rest: true });
  }
  return { slices, count: sorted.length };
}

const pctText = w => `${w.toFixed(1)}%`;

export default function AllocationRing({ rows = [], holdings = null, size = 128, stroke = 18 }) {
  const { slices, count: sliced } = ringSlices(rows);
  const count = holdings ?? sliced;
  if (!slices.length) return null;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = slices.reduce((a, s) => a + s.weight, 0) || 1;
  // A hairline of the card's own ground between slices, so two neighbours
  // never merge: the gap is left unpainted, and the card shows through it in
  // both themes (none with a single slice: a full ring has no seam).
  const gap = slices.length > 1 ? Math.min(2, c * 0.006) : 0;
  let at = 0;
  const arcs = slices.map(s => {
    const len = (s.weight / total) * c;
    // A holding too small for its own gap still gets a visible sliver. Each
    // stroke is centred on its own arc, so the gap falls half on each side
    // and a sliver never runs over its neighbour's seam (review).
    const dash = Math.max(1.5, len - gap);
    const arc = { ...s, dash, offset: -(at + (len - dash) / 2) };
    at += len;
    return arc;
  });
  // A screen reader hears the middle ("12 החזקות") and the legend, once each:
  // the drawing itself is hidden from it.
  return (
    <div data-ring style={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 18, rowGap: 12, minWidth: 0 }}>
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true"
          style={{ display: "block", transform: "rotate(-90deg)" }}>
          {arcs.map(a => (
            <circle key={a.key} data-slice={a.key} cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={a.color} strokeWidth={stroke}
              strokeDasharray={`${a.dash} ${c - a.dash}`} strokeDashoffset={a.offset}/>
          ))}
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", lineHeight: 1.1 }}>
          <LTR className="si-num" style={{ fontSize: Math.round(size * 0.22), fontWeight: 600, color: UI.text.primary }}>{count}</LTR>
          <span style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 2 }}>{count === 1 ? "החזקה" : "החזקות"}</span>
        </div>
      </div>

      {/* Grows up to 150px beside the ring, and moves under it on a phone too
          narrow for both (320px). */}
      <ul data-ring-legend aria-label="חלוקת התיק" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 5, minWidth: 130, flex: "0 1 auto" }}>
        {slices.map(s => (
          <li key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, lineHeight: 1.35, minWidth: 0 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 999, background: s.color, flexShrink: 0 }}/>
            <span style={{ fontWeight: 600, color: UI.text.primary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.rest ? <>אחרות <span style={{ fontWeight: 400, color: UI.text.muted }}>({s.count === 1 ? "אחת" : <LTR className="si-num">{s.count}</LTR>})</span></> : <LTR>{s.label}</LTR>}
            </span>
            {/* The percent right beside its ticker (sprint 17): across a gap
                the eye had to travel to pair them. */}
            <LTR className="si-num" style={{ color: UI.text.primary, fontWeight: 500, flexShrink: 0 }}>{pctText(s.weight)}</LTR>
          </li>
        ))}
      </ul>
    </div>
  );
}
